import { TrendingUp, TrendingDown, ArrowUpRight } from 'lucide-react';

const caseStudies = [
  {
    industry: 'SaaS / B2B',
    company: 'Enterprise Software Company',
    challenge: 'High CPL with low-quality leads from Google Ads — sales team wasting time on unqualified prospects.',
    results: [
      { metric: 'Cost Per Lead', before: '$187', after: '$62', change: '-67%', positive: true },
      { metric: 'Qualified Leads', before: '23/mo', after: '94/mo', change: '+309%', positive: true },
      { metric: 'Pipeline Value', before: '$120K', after: '$890K', change: '+642%', positive: true },
      { metric: 'ROAS', before: '1.8x', after: '6.2x', change: '+244%', positive: true },
    ],
    color: 'from-blue-500 to-cyan-500',
    borderColor: 'border-blue-500/20',
  },
  {
    industry: 'E-Commerce',
    company: 'Premium D2C Brand',
    challenge: 'Scaling Meta Ads profitably beyond $50K/month spend while maintaining brand positioning.',
    results: [
      { metric: 'Ad Spend', before: '$52K/mo', after: '$210K/mo', change: '+304%', positive: true },
      { metric: 'ROAS', before: '2.1x', after: '4.8x', change: '+129%', positive: true },
      { metric: 'Revenue', before: '$109K', after: '$1.01M', change: '+827%', positive: true },
      { metric: 'CPA', before: '$89', after: '$41', change: '-54%', positive: true },
    ],
    color: 'from-violet-500 to-purple-500',
    borderColor: 'border-violet-500/20',
  },
  {
    industry: 'Financial Services',
    company: 'Wealth Management Firm',
    challenge: 'Needed high-net-worth lead generation through compliant digital advertising channels.',
    results: [
      { metric: 'Qualified Leads', before: '8/mo', after: '47/mo', change: '+488%', positive: true },
      { metric: 'Cost Per Lead', before: '$420', after: '$156', change: '-63%', positive: true },
      { metric: 'Close Rate', before: '12%', after: '28%', change: '+133%', positive: true },
      { metric: 'Revenue/Lead', before: '$2.1K', after: '$8.7K', change: '+314%', positive: true },
    ],
    color: 'from-emerald-500 to-teal-500',
    borderColor: 'border-emerald-500/20',
  },
];

export default function ResultsSection() {
  return (
    <section id="results" data-testid="results-section" className="section-padding bg-white relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-vertex-500/10 border border-vertex-500/20 mb-6">
            <span className="text-vertex-600 text-sm font-semibold">Proven Results</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-vertex-900 mb-5">
            Numbers That
            <span className="gradient-text"> Speak Volumes</span>
          </h2>
          <p className="text-gray-600 text-lg leading-relaxed">
            Real performance data from real client engagements. No vanity metrics — just business impact.
          </p>
        </div>

        {/* Case Studies */}
        <div className="space-y-8">
          {caseStudies.map((study, idx) => (
            <div
              key={study.industry}
              className={`group bg-gradient-to-br from-gray-50 to-white rounded-3xl border ${study.borderColor} hover:border-opacity-50 p-8 md:p-10 card-hover`}
            >
              <div className="flex flex-col lg:flex-row gap-8">
                {/* Left - Info */}
                <div className="lg:w-1/3">
                  <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r ${study.color} text-white mb-4`}>
                    {study.industry}
                  </span>
                  <h3 className="text-xl font-display font-bold text-vertex-900 mb-3">{study.company}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{study.challenge}</p>
                </div>

                {/* Right - Metrics */}
                <div className="lg:w-2/3 grid grid-cols-2 md:grid-cols-4 gap-4">
                  {study.results.map((result) => (
                    <div key={result.metric} className="bg-white rounded-xl p-4 border border-gray-100 group-hover:shadow-md transition-shadow duration-300">
                      <div className="text-xs text-gray-400 font-medium mb-2">{result.metric}</div>
                      <div className="flex items-baseline gap-1.5 mb-1">
                        <span className="text-xs text-gray-400 line-through">{result.before}</span>
                        <ArrowUpRight className="w-3 h-3 text-vertex-500" />
                      </div>
                      <div className="text-2xl font-display font-bold text-vertex-900">{result.after}</div>
                      <div className={`inline-flex items-center gap-1 mt-1 text-xs font-semibold ${
                        result.change.startsWith('+') ? 'text-emerald-600' : 'text-emerald-600'
                      }`}>
                        <TrendingUp className="w-3 h-3" />
                        {result.change}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
